// New customer form. Default mode is schema-driven (fetches the ERP
// customer schema and renders typed fields). When the configured
// endpoint is set to "custom", the form falls back to a raw JSON body
// editor — the user types the POST payload directly. On success the
// created record is written to the configured outputBindings.

import { useEffect, useState } from 'react'
import { useRuntime } from '@/pages/workbench/smartpage-preview/RuntimeContext'
import type { NewCustomerFormBlock } from '@/pages/designers/page/types'
import {
  apiPost, erpErrorMessage, erpFieldErrors, getCustomerSchema,
  type ErpFieldDef,
} from '../erpService'
import { outputVarName, writeOutputs } from '../bindingResolver'
import { resolveEndpointUrl } from './endpoints'
import { injectEcomStyles } from './styles'

function inputType(f: ErpFieldDef): string {
  if (f.validate === 'email') return 'email'
  if (f.validate === 'phone') return 'tel'
  if (f.type === 'number') return 'number'
  return 'text'
}

export default function NewCustomerFormRenderer({ block }: { block: NewCustomerFormBlock }) {
  injectEcomStyles()
  const runtime = useRuntime()

  // Source kind: schema-driven (customers endpoint) vs raw JSON (custom URL).
  const isCustom = block.source.endpointId === 'custom'

  const [fields, setFields] = useState<ErpFieldDef[]>([])
  const [values, setValues] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loadingSchema, setLoadingSchema] = useState(false)
  const [schemaError, setSchemaError] = useState('')
  const [rawBody, setRawBody] = useState('{\n  \n}')
  const [rawError, setRawError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [localVisible, setLocalVisible] = useState(true)

  const toggleVarName = outputVarName(block.toggleBinding)
  const toggleVal = toggleVarName ? runtime.vars[toggleVarName] : undefined
  const visible = toggleVarName ? !!toggleVal : localVisible

  useEffect(() => {
    if (isCustom) return  // raw mode: no schema fetch
    setLoadingSchema(true)
    getCustomerSchema()
      .then((schema) => {
        setFields(schema.fields || [])
        const seeds: Record<string, string> = {}
        for (const f of schema.fields || []) seeds[f.key] = ''
        setValues(seeds)
        setLoadingSchema(false)
      })
      .catch((err) => {
        setSchemaError(erpErrorMessage(err, 'Failed to load form.'))
        setLoadingSchema(false)
      })
  }, [isCustom])

  if (!visible) return null

  function cancel() {
    if (toggleVarName) runtime.setVar(toggleVarName, false)
    setSubmitError('')
    setErrors({})
    if (!toggleVarName) setLocalVisible(false)
  }

  function postPayload(payload: unknown) {
    const url = resolveEndpointUrl(block.source)
    if (!url) {
      setSubmitError('No endpoint configured. Set a URL in the block source.')
      return
    }
    setSubmitting(true)
    setSubmitError('')
    setErrors({})
    apiPost(url, payload)
      .then((record) => {
        writeOutputs(
          block.outputBindings,
          { value: record, ...(record && typeof record === 'object' ? record : {}) },
          runtime.setVar,
        )
        if (toggleVarName) runtime.setVar(toggleVarName, false)
        else setLocalVisible(false)
        setSubmitting(false)
      })
      .catch((err) => {
        const fieldErrs = erpFieldErrors(err)
        if (fieldErrs) setErrors(fieldErrs)
        setSubmitError(erpErrorMessage(err, 'Failed to create record.'))
        setSubmitting(false)
      })
  }

  function submitSchemaForm(ev: React.FormEvent) {
    ev.preventDefault()
    if (submitting) return
    const localErrors: Record<string, string> = {}
    for (const f of fields) {
      const v = (values[f.key] ?? '').trim()
      if (f.required && !v) localErrors[f.key] = 'Required'
    }
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors)
      return
    }
    const payload: Record<string, string> = {}
    for (const f of fields) payload[f.key] = (values[f.key] ?? '').trim()
    postPayload(payload)
  }

  function submitRawJson(ev: React.FormEvent) {
    ev.preventDefault()
    if (submitting) return
    let parsed: unknown
    try {
      parsed = JSON.parse(rawBody)
    } catch (e) {
      setRawError((e as Error).message || 'Invalid JSON')
      return
    }
    setRawError('')
    postPayload(parsed)
  }

  // ── Raw JSON fallback (custom URL) ──
  if (isCustom) {
    const url = resolveEndpointUrl(block.source)
    return (
      <section className="ncf">
        <header className="ncf__head">
          <h3 className="ncf__title">Submit to custom endpoint</h3>
          <button type="button" className="ncf__close" onClick={cancel} aria-label="Cancel">×</button>
        </header>
        <form onSubmit={submitRawJson} className="ncf__grid">
          <div className="ncf__field ncf__field--full">
            <span className="ncf__label">POST {url || '(no URL)'}</span>
            <textarea
              className="ncf__input ncf__textarea"
              value={rawBody}
              onChange={(e) => setRawBody(e.currentTarget.value)}
              rows={10}
              style={{ fontFamily: 'monospace' }}
              spellCheck={false}
              placeholder='{\n  "name": "...",\n  "phone": "..."\n}'
            />
            {rawError && <span className="ncf__fielderr">{rawError}</span>}
          </div>
          {submitError && <div className="ncf__error ncf__error--full">{submitError}</div>}
          <div className="ncf__actions">
            <button type="button" className="ncf__btn ncf__btn--ghost" onClick={cancel} disabled={submitting}>Cancel</button>
            <button type="submit" className="ncf__btn ncf__btn--primary" disabled={submitting || !url}>
              {submitting ? 'Sending…' : (block.submitLabel || 'Send')}
            </button>
          </div>
        </form>
      </section>
    )
  }

  // ── Schema-driven form (built-in endpoints) ──
  return (
    <section className="ncf">
      <header className="ncf__head">
        <h3 className="ncf__title">New customer</h3>
        <button type="button" className="ncf__close" onClick={cancel} aria-label="Cancel">×</button>
      </header>

      {loadingSchema ? (
        <div className="ncf__hint">Loading form…</div>
      ) : schemaError ? (
        <div className="ncf__error">{schemaError}</div>
      ) : (
        <form
          className={`ncf__grid${block.layout === 'two-column' ? ' ncf__grid--two' : ''}`}
          onSubmit={submitSchemaForm}
        >
          {fields.map((f) => (
            <label
              key={f.key}
              className={`ncf__field${f.type === 'textarea' ? ' ncf__field--full' : ''}`}
            >
              <span className="ncf__label">
                {f.label}
                {f.required && <span className="ncf__req">*</span>}
              </span>
              {f.type === 'textarea' ? (
                <textarea
                  className="ncf__input ncf__textarea"
                  name={f.key}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.currentTarget.value }))}
                  placeholder={f.placeholder || ''}
                  rows={3}
                />
              ) : (
                <input
                  className="ncf__input"
                  name={f.key}
                  type={inputType(f)}
                  value={values[f.key] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.currentTarget.value }))}
                  placeholder={f.placeholder || ''}
                  autoComplete="off"
                />
              )}
              {errors[f.key] && <span className="ncf__fielderr">{errors[f.key]}</span>}
            </label>
          ))}
          {submitError && <div className="ncf__error ncf__error--full">{submitError}</div>}
          <div className="ncf__actions">
            <button
              type="button"
              className="ncf__btn ncf__btn--ghost"
              onClick={cancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="ncf__btn ncf__btn--primary"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : (block.submitLabel || 'Save customer')}
            </button>
          </div>
        </form>
      )}
    </section>
  )
}
